import type { Row, SqlStatement, SqlValue, StorageAdapter } from "./adapter.js";

/**
 * Adapter backed by @capacitor-community/sqlite's NATIVE plugin. The bridge is
 * reached via registerPlugin("CapacitorSQLite") from @capacitor/core — the
 * community package's JS wrapper is deliberately not imported (its sugar adds
 * nothing over the raw method calls and would bloat the web bundle). The
 * native implementation is wired into the Android project by `cap sync` from
 * apps/mobile's dependency on @capacitor-community/sqlite.
 *
 * @capacitor/core is imported lazily so Vite splits it into a chunk that
 * browser/Tauri builds never fetch.
 */

interface CapSqliteBridge {
  createConnection(o: { database: string; version: number; encrypted: boolean; mode: string; readonly: boolean }): Promise<void>;
  open(o: { database: string; readonly: boolean }): Promise<void>;
  closeConnection(o: { database: string; readonly: boolean }): Promise<void>;
  run(o: { database: string; statement: string; values: unknown[]; transaction: boolean; readonly: boolean }): Promise<{ changes?: { changes?: number } }>;
  executeSet(o: { database: string; set: Array<{ statement: string; values: unknown[] }>; transaction: boolean; readonly: boolean; returnMode: string }): Promise<{ changes?: { changes?: number } }>;
  query(o: { database: string; statement: string; values: unknown[]; readonly: boolean }): Promise<{ values?: unknown[] }>;
  isSecretStored(): Promise<{ result?: boolean }>;
  setEncryptionSecret(o: { passphrase: string }): Promise<void>;
  isDatabase(o: { database: string }): Promise<{ result?: boolean }>;
  isDatabaseEncrypted(o: { database: string }): Promise<{ result?: boolean }>;
}

/** Connection parameters chosen by {@link prepareEncryption}. */
interface ConnectionMode {
  encrypted: boolean;
  mode: "secret" | "encryption" | "no-encryption";
}

/** SQLite has no boolean affinity; the native bridge rejects raw JS booleans. */
export function bindable(params: ReadonlyArray<SqlValue | undefined>): unknown[] {
  return params.map((p) => {
    if (p === undefined) return null;
    return typeof p === "boolean" ? (p ? 1 : 0) : p;
  });
}

export class CapacitorSqliteAdapter implements StorageAdapter {
  private bridge: CapSqliteBridge | null = null;
  constructor(private readonly database: string = "reminders") {}

  async open(): Promise<void> {
    if (this.bridge) return;
    const { registerPlugin } = await import("@capacitor/core");
    const bridge = registerPlugin<CapSqliteBridge>("CapacitorSQLite");
    const conn = await prepareEncryption(bridge, this.database);
    try {
      await bridge.createConnection({ database: this.database, version: 1, encrypted: conn.encrypted, mode: conn.mode, readonly: false });
    } catch (e) {
      // A still-live connection from a previous JS context (WebView reload) is fine to reuse.
      if (!String(e).toLowerCase().includes("already exists")) throw e;
    }
    await bridge.open({ database: this.database, readonly: false });
    this.bridge = bridge;
  }

  async close(): Promise<void> {
    if (!this.bridge) return;
    await this.bridge.closeConnection({ database: this.database, readonly: false });
    this.bridge = null;
  }

  async execute(sql: string, params: ReadonlyArray<SqlValue> = []): Promise<{ rowsAffected: number }> {
    await this.ensure();
    const res = await this.bridge!.run({ database: this.database, statement: sql, values: bindable(params), transaction: false, readonly: false });
    return { rowsAffected: res.changes?.changes ?? 0 };
  }

  async executeBatch(statements: ReadonlyArray<SqlStatement>): Promise<{ rowsAffected: number }> {
    if (statements.length === 0) return { rowsAffected: 0 };
    await this.ensure();
    const res = await this.bridge!.executeSet({
      database: this.database,
      set: statements.map((s) => ({ statement: s.sql, values: bindable(s.params ?? []) })),
      transaction: true,
      readonly: false,
      returnMode: "no",
    });
    return { rowsAffected: res.changes?.changes ?? 0 };
  }

  async select<T = Row>(sql: string, params: ReadonlyArray<SqlValue> = []): Promise<T[]> {
    await this.ensure();
    const res = await this.bridge!.query({ database: this.database, statement: sql, values: bindable(params), readonly: false });
    return (res.values ?? []) as T[];
  }

  // TRAP: a registerPlugin() proxy must NEVER be the resolution value of a
  // promise (e.g. returned from an async function) — resolving probes `.then`,
  // which the proxy forwards to the bridge as a native call:
  // `"CapacitorSQLite.then()" is not implemented on android`. So ensure()
  // resolves with void and callers read `this.bridge` directly.
  private async ensure(): Promise<void> {
    if (!this.bridge) await this.open();
  }
}

/**
 * SQLCipher-at-rest (Android). The plugin stores the passphrase in
 * EncryptedSharedPreferences wrapped by an Android-Keystore AES256-GCM master
 * key, so we generate a random one once and never see it again. Modes (verified
 * against the plugin's native source, v7.0.3):
 *   - "secret"      opens an ENCRYPTED db with the stored passphrase (and
 *                    creates a fresh db encrypted when the file doesn't exist);
 *   - "encryption"  encrypts an existing PLAINTEXT db in place (safe
 *                    export-to-temp-then-rename), then opens it — the one-time
 *                    migration path for pre-encryption installs.
 * Requires `androidIsEncryption: true` in capacitor.config.json — without it
 * every secret call throws, and we fall back to an unencrypted open below so an
 * odd device (keystore failure) degrades to the old behavior instead of
 * bricking startup. NOTE: an already-encrypted db can't open unencrypted; that
 * failure surfaces to platform.ts's IndexedDB fallback (empty view, data intact
 * on next successful start).
 */
async function prepareEncryption(bridge: CapSqliteBridge, database: string): Promise<ConnectionMode> {
  try {
    const stored = await bridge.isSecretStored();
    if (stored.result !== true) {
      await bridge.setEncryptionSecret({ passphrase: randomPassphrase() });
    }
    const exists = (await bridge.isDatabase({ database })).result === true;
    if (!exists) return { encrypted: true, mode: "secret" };
    const isEncrypted = (await bridge.isDatabaseEncrypted({ database })).result === true;
    return { encrypted: true, mode: isEncrypted ? "secret" : "encryption" };
  } catch (e) {
    console.error("sqlite encryption unavailable; opening unencrypted", e);
    return { encrypted: false, mode: "no-encryption" };
  }
}

/** 256 bits of WebCrypto randomness as hex — the once-only generated passphrase. */
function randomPassphrase(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
